import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import {
    FormControl,
    FormControlLabel,
    makeStyles,
    Theme,
    Typography,
    FormGroup as MuiFormGroup,
} from '@material-ui/core';

import { Page } from '../../components/Page';
import { BackAppBar } from '../../components/BackAppBar';
import { PageBody } from '../../components/PageBody';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { NotifyWriteRequestData } from '../../api/notifyWrite';
import { useSetting } from '../../units/setting';
import { Checkbox } from '../../components/Checkbox';
import { api } from '../../api';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { useLoading } from '../../units/loading';
import { useAlert } from '../../units/alert';
import { usePartner } from '../../hooks/usePartner';

type SetAlertPageProps = {};

SetAlertPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        marginBottom: theme.spacing(2),
        '& fieldset': {
            margin: theme.spacing(1, 0),
        },
    },
    title: {
        marginBottom: theme.spacing(2),
        color: theme.palette.mainColor,
        fontSize: theme.typography.h6.fontSize,
        fontWeight: 'bold',
    },
    checkboxRow: {
        padding: theme.spacing(0, 1),
        '& .MuiFormControlLabel-root': {
            flex: 1,
        },
    },
    groupTitle: {
        fontSize: 18,
    },
}));

function SetAlertPage(props: SetAlertPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const { setting } = useSetting();
    const { setIsLoading } = useLoading();
    const alert = useAlert();
    const [formData, setFormData] = useState<NotifyWriteRequestData>({
        line_uid: '',
        login: '',
        line_accepted: false,
        mail_accepted: false,
        line_picked_up: false,
        mail_picked_up: false,
        line_under_repair: false,
        mail_under_repair: false,
        line_repair_done: false,
        mail_repair_done: false,
        line_shipped: false,
        mail_shipped: false,
    });
    const { partner, isLoading: isPartnerLoading, partnerLineUid } = usePartner();

    useEffect(() => {
        setIsLoading(isPartnerLoading);
    }, [isPartnerLoading]);

    useEffect(() => {
        if (partner) {
            api.notify({
                line_uid: partnerLineUid || '',
                login: partner?.email || '',
            })
                .then((data) => {
                    const { status, msg, ...notifyData } = data;
                    setFormData({
                        ...formData,
                        ...notifyData,
                        login: setting.loginInfo.login,
                    });
                });
        }
    }, [partner]);

    function handleSubmitClick() {
        setIsLoading(true);
        api.notifyWrite(formData)
            .then(() => {
                setIsLoading(false);
                alert.show({
                    type: 'success',
                    title: '操作成功',
                    message: '已成功修改提醒設定',
                });
                history.replace('/member/action');
            })
            .catch(async (e) => {
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
    }

    return (
        <Page>
            <BackAppBar title="提醒設定" onBackClick={() => history.replace('/member/action')} />
            <PageBody>
                <Card className={classes.card}>
                    <Typography className={classes.title}>選擇要通知的站點與方式</Typography>
                    <FormControl component="fieldset">
                        <Typography className={classes.groupTitle}>已受理</Typography>
                        <MuiFormGroup className={classes.checkboxRow} row>
                            <Checkbox
                                label="E-mail"
                                checked={formData.mail_accepted}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    mail_accepted: checked,
                                })}
                            />
                            <Checkbox
                                label="LINE"
                                checked={formData.line_accepted}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    line_accepted: checked,
                                })}
                            />
                        </MuiFormGroup>
                    </FormControl>
                    <FormControl component="fieldset">
                        <Typography className={classes.groupTitle}>已取件</Typography>
                        <MuiFormGroup className={classes.checkboxRow} row>
                            <Checkbox
                                label="E-mail"
                                checked={formData.mail_picked_up}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    mail_picked_up: checked,
                                })}
                            />
                            <Checkbox
                                label="LINE"
                                checked={formData.line_picked_up}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    line_picked_up: checked,
                                })}
                            />
                        </MuiFormGroup>
                    </FormControl>
                    <FormControl component="fieldset">
                        <Typography className={classes.groupTitle}>維修中</Typography>
                        <MuiFormGroup className={classes.checkboxRow} row>
                            <Checkbox
                                label="E-mail"
                                checked={formData.mail_under_repair}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    mail_under_repair: checked,
                                })}
                            />
                            <Checkbox
                                label="LINE"
                                checked={formData.line_under_repair}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    line_under_repair: checked,
                                })}
                            />
                        </MuiFormGroup>
                    </FormControl>
                    <FormControl component="fieldset">
                        <Typography className={classes.groupTitle}>已完修</Typography>
                        <MuiFormGroup className={classes.checkboxRow} row>
                            <Checkbox
                                label="E-mail"
                                checked={formData.mail_repair_done}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    mail_repair_done: checked,
                                })}
                            />
                            <Checkbox
                                label="LINE"
                                checked={formData.line_repair_done}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    line_repair_done: checked,
                                })}
                            />
                        </MuiFormGroup>
                    </FormControl>
                    <FormControl component="fieldset">
                        <Typography className={classes.groupTitle}>已出貨</Typography>
                        <MuiFormGroup className={classes.checkboxRow} row>
                            <Checkbox
                                label="E-mail"
                                checked={formData.mail_shipped}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    mail_shipped: checked,
                                })}
                            />
                            <Checkbox
                                label="LINE"
                                checked={formData.line_shipped}
                                onChange={(e, checked) => setFormData({
                                    ...formData,
                                    line_shipped: checked,
                                })}
                            />
                        </MuiFormGroup>
                    </FormControl>
                </Card>
                <Button onClick={handleSubmitClick}>完成設定</Button>
            </PageBody>
        </Page>
    );
}

export { SetAlertPage };
